/**
 * Decisions about this item, on the item page's Prioritization destination.
 *
 * Reads the same assembled trail as the "Why is this #N?" inspector
 * (fetchDecisionTrail, canon 27 §5) and shows its recorded decisions inline:
 * what was decided, by whom, when, and the reason — or that no reason was
 * recorded. Recording a new decision opens the Decisions page with this item
 * attached (/prioritization/decisions?attachItem=…); the full trail opens in
 * the existing inspector.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Callout, EmptyState } from '@/components/ds';
import { LABEL_MONO, btnSecondary } from '@/components/mono/primitives';
import { formatDate } from '@/i18n/formatters';
import { fetchDecisionTrail, type DecisionTrailEntry } from '@/lib/decisionTrailService';

const SHOWN_KINDS = new Set<DecisionTrailEntry['kind']>(['decision', 'plan_commitment', 'list_published']);
const SHORT_DATE: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
/** A decision no longer in force (canon: active → superseded | reversed) is drawn lighter and smaller. */
const NOT_IN_FORCE: Record<string, string> = { superseded: 'Superseded', reversed: 'Reversed' };

export function ItemDecisionsSection({ itemId, onOpenTrail }: { itemId: string; onOpenTrail: () => void }) {
  const [entries, setEntries] = useState<DecisionTrailEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setEntries(null);
    setError(null);
    fetchDecisionTrail(itemId)
      .then(({ entries: all, error: e }) => {
        if (!live) return;
        if (e) setError(e);
        setEntries(
          all
            .filter((x) => SHOWN_KINDS.has(x.kind))
            .sort((a, b) => (b.at ?? '').localeCompare(a.at ?? '')),
        );
      })
      .catch((e: unknown) => {
        if (live) setError(e instanceof Error ? e.message : 'Could not load decisions.');
      });
    return () => {
      live = false;
    };
  }, [itemId]);

  return (
    <div className="space-y-3" data-testid="item-decisions-section">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <p className={LABEL_MONO}>Decisions about this item</p>
        <div className="flex flex-wrap gap-2">
          <Link to={`/prioritization/decisions?attachItem=${encodeURIComponent(itemId)}`} {...btnSecondary('!h-8')}>
            Record a decision
          </Link>
          <button type="button" {...btnSecondary('!h-8')} onClick={onOpenTrail}>
            Full trail
          </button>
        </div>
      </div>

      {error && (
        <Callout tone="coral" label="Decisions" title="Could not load every decision">
          {error}
        </Callout>
      )}

      {entries === null && !error && <p className="text-sm text-[var(--text-dim)]">Loading decisions…</p>}

      {entries && entries.length === 0 && !error && (
        <EmptyState
          title="No decisions recorded yet"
          description="When someone records a decision that names this item, or publishes a list it is on, it shows here with who decided and why."
        />
      )}

      {entries && entries.length > 0 && (
        <ul className="border-t" style={{ borderColor: 'var(--rule)' }}>
          {entries.map((e) => {
            const gone = NOT_IN_FORCE[String(e.detail?.status ?? '')];
            const titleClass = gone ? 'text-[13.5px] text-[var(--text-dim)]' : 'text-[var(--text-pri)]';
            return (
              <li key={`${e.sourceTable}:${e.sourceId}`} className="border-b py-2.5" style={{ borderColor: 'var(--rule)' }} data-in-force={gone ? 'no' : 'yes'}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="flex flex-wrap items-baseline gap-2">
                    {e.decisionId ? (
                      <Link to={`/prioritization/decisions/${e.decisionId}`} className={`${titleClass} hover:underline`}>
                        {e.title}
                      </Link>
                    ) : (
                      <span className={titleClass}>{e.title}</span>
                    )}
                    {gone && <span className={LABEL_MONO}>{gone} · not in force</span>}
                  </span>
                  <span className="text-[13px] text-[var(--text-dim)]">
                    {e.at ? formatDate(e.at, SHORT_DATE) : 'Date not recorded'}
                    {e.actorName ? ` · ${e.actorName}` : ''}
                  </span>
                </div>
                <p className={gone ? 'mt-0.5 text-[12.5px]' : 'mt-0.5 text-[13.5px]'} style={{ color: e.why && !gone ? 'var(--text-sec)' : 'var(--text-dim)' }}>
                  {e.why ?? 'No reason was recorded.'}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default ItemDecisionsSection;
